import * as React from "react";

import { Select } from "@/components/ui/select";
import { cn } from "@/lib/utils";

/**
 * Where a row of pills stops being better than a dropdown.
 *
 * Eight is the run workspace header's budget, not a taste: there these pills
 * sit in a `min-w-[12rem] flex-1` column beside the view toggle, so a ninth id
 * wraps the row and starts pushing the plots down — which is the crowding the
 * focused view exists to undo. Fragment and torsion counts on these surfaces
 * are 2-5 in practice, so the dropdown is the safety valve for a big fragment,
 * not the expected case.
 */
export const MAX_PILLS = 8;

/**
 * One axis of the nested selection: a pill per item, or a dropdown when the
 * axis is longer than `MAX_PILLS`.
 *
 * **Shared by every surface that picks along an axis** (VIZ U5.4) — the run
 * workspace's torsion plots and the Analysis tab's per-dihedral grid. It lives
 * in its own module for the same reason `DihedralRMSDGrid` is not forked per
 * surface: a second picker is a second set of keyboard semantics, a second
 * pill-vs-dropdown threshold and a second accent, and those drift apart the
 * moment one of them is touched.
 *
 * **Pills are `role="radio"` in a `radiogroup`, not `aria-pressed` toggles.**
 * The axis is a single-choice set — exactly one item is on screen — and that is
 * what a radiogroup means to a screen reader; a row of pressed/unpressed
 * toggles says "several independent switches, some of which happen to be off"
 * and would announce the selection as a state rather than as a choice among
 * this fragment's torsions. It also brings the interaction users already expect
 * from that role, which is the second half of the requirement: **one tab stop
 * for the whole group** (roving `tabIndex`, only the checked pill is tabbable)
 * and arrows/Home/End within it, wrapping at the ends. Focus follows selection,
 * as the radiogroup pattern specifies.
 *
 * **The long-axis fallback is the `ui/select` primitive — the native control —
 * NOT `@radix-ui/react-select`.** The dependency is installed, and that is not
 * a mandate to use it: a portalled listbox here would be a second way to
 * express the app's select, which is precisely what U3.1's primitive and its
 * adoption bar exist to prevent, and this call site needs nothing native cannot
 * do (a flat list of ids, no groups, no icons, no search). Native also keeps
 * the platform picker, `<option>` semantics and type-ahead for free — on a
 * 9-to-N-item list, type-ahead is the affordance that matters.
 *
 * The position is derived from the id on every render — the picker stores
 * nothing. An axis with a single value renders that one pill: it costs a row
 * the caption already occupies, and it keeps "the set is visible" true at every
 * count rather than only above one.
 */
export function AxisPicker({
  axisLabel,
  values,
  value,
  onChange,
}: {
  axisLabel: string;
  values: readonly string[];
  value: string | null;
  onChange: (next: string) => void;
}): JSX.Element | null {
  const labelId = React.useId();
  const groupRef = React.useRef<HTMLDivElement>(null);

  if (value === null || values.length === 0) return null;
  const index = values.indexOf(value);
  // A value that is not in its own axis means the caller resolved against a
  // different list than this one; say nothing rather than mark a pill that is
  // not the selection.
  if (index < 0) return null;

  const moveTo = (next: number): void => {
    const target = values[next];
    if (target === undefined) return;
    onChange(target);
    // Focus moves with the selection, which is what makes the roving tabIndex
    // coherent: the pill that is now the group's single tab stop is the one the
    // keyboard is on. Read out of the DOM rather than through a ref per pill —
    // the nodes are keyed by value and outlive this render.
    groupRef.current?.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus();
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>): void => {
    const last = values.length - 1;
    let next: number;
    switch (event.key) {
      case "ArrowRight":
      case "ArrowDown":
        next = index === last ? 0 : index + 1;
        break;
      case "ArrowLeft":
      case "ArrowUp":
        next = index === 0 ? last : index - 1;
        break;
      case "Home":
        next = 0;
        break;
      case "End":
        next = last;
        break;
      default:
        return;
    }
    // Only once a key is known to be ours: arrows must not also scroll the
    // panel, but every other key (Tab, and the browser's own shortcuts) has to
    // keep working.
    event.preventDefault();
    moveTo(next);
  };

  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-baseline gap-x-2 text-[11px] text-muted-foreground">
        <span id={labelId} className="uppercase tracking-wider">
          {axisLabel}
        </span>
        <span className="font-mono text-foreground">{value}</span>
        <span className="tabular-nums">
          {index + 1} of {values.length}
        </span>
      </div>
      {values.length <= MAX_PILLS ? (
        <div
          ref={groupRef}
          role="radiogroup"
          aria-labelledby={labelId}
          className="flex flex-wrap items-center gap-1.5"
        >
          {values.map((item, position) => (
            <button
              key={item}
              type="button"
              role="radio"
              aria-checked={position === index}
              tabIndex={position === index ? 0 : -1}
              // `moveTo`, not a bare `onChange`: it selects AND moves focus, so
              // a click leaves the keyboard where the eye is and the next arrow
              // continues from the pill the user just picked (flowstack#1088).
              // The click path used to skip the focus half, which is not
              // theoretical — Safari and Firefox on macOS do not focus a button
              // on click, so there the selection and the focus ring genuinely
              // came apart.
              onClick={() => moveTo(position)}
              onKeyDown={onKeyDown}
              className={cn(
                "rounded-full border px-2.5 py-0.5 font-mono text-[11px] transition-colors",
                position === index
                  ? // The cyan edge is the marker the overview grid already puts
                    // on the selected panel (`ring-cyan/70`, `DihedralRMSDGrid`)
                    // — same accent, so the pill and the panel it selects say
                    // "this one" the same way. Not a new colour: the surface
                    // pair is the view toggle's, beside it.
                    "border-cyan/60 bg-surface-3 text-foreground"
                  : "border-border-subtle/60 bg-surface-2 text-muted-foreground hover:text-foreground",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background"
              )}
            >
              {item}
            </button>
          ))}
        </div>
      ) : (
        <Select
          size="sm"
          aria-labelledby={labelId}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          className="w-full font-mono text-[11px]"
        >
          {values.map((item) => (
            <option key={item} value={item}>
              {item}
            </option>
          ))}
        </Select>
      )}
    </div>
  );
}
